import { ref } from '@vue/composition-api'
import ClubFeatureFlagApi from '@/api/waiter/ClubFeatureFlag'
import useCurrentData from '@/views/composable/useCurrentData'

// NOTE: β機能フラグは「店舗の設定」。以前はユーザー(ブラウザ)単位の localStorage に持っていたが、
// 同じ店舗の全端末で挙動が揃うようにサーバー（clubs.feature_flags）へ移した。
// localStorage は初期描画用のキャッシュとしてのみ使う（真の所在はサーバー）。
//
// 消費側（Floor.vue / Halls.vue / TableShowDrawer.vue）は同期的な flags ref を読むだけでよいように、
// 「キャッシュで即描画 → fetch 完了で差し替え」の2段構えにしている。
//
// 取得タイミング（「設定したのに反映されない」を防ぐための多層）:
//   - レイアウトのマウント時（LayoutWaiter / LayoutAdmin / LayoutCustom）… リロード・日次の起動
//   - 店舗切替時 … VerticalNavMenuCurrentClub の店舗切替はページリロードを伴うので上と同じ経路になる
//   - タブ復帰時（visibilitychange）… 開きっぱなしのフロア端末
// 開きっぱなしの端末への即時反映まではしない（必要になったら NotificationChannel で push する）。
//
// フラグを増やすときはバックエンドの ClubFeatureFlags::DEFINITIONS にキーを足すだけでよい。
// 切替UI（店舗設定 / admin_v2）は definitions のループ描画なので触らなくてよく、DEFAULT_FLAGS への
// 追加はフロントのコードがそのフラグを読むとき（fetch 前の既定値が必要なとき）だけ必要。
//
// 紛らわしい別物: store/admin/selfOrderSettings.js の featureFlags はセルフオーダー設定の別機構。

const STORAGE_KEY = 'clubFeatureFlags'

// 旧: ユーザー(ブラウザ)単位のフラグ。移行期間中は、この端末に残っている ON を店舗設定へ引き継ぐ。
// 引き継ぎ後はキーを削除するので1端末1回きり。詳しくは migrateLegacyFeatureFlags のコメント参照。
// TODO(移行完了後に撤去): 数リリース後に LEGACY_STORAGE_KEY と移行処理・掃除処理を消す
const LEGACY_STORAGE_KEY = 'featureFlags'

// サーバーから取得できるまでの既定値。どんなフラグが存在するかの真の所在はバックエンドの
// ClubFeatureFlags::DEFINITIONS で、ここには「このフロントのコードが読むフラグ」だけを並べる。
// （サーバーが返した見知らぬキーも flags には保持するので、BE先行でフラグを足しても切替UIは動く）
const DEFAULT_FLAGS = {
  // 卓作成ステップ方式ウィザード（既存 TableCreateDrawer と置き換え可能）
  useTableCreateWizard: false,

  // 卓詳細ドロワー概要タブの3カラムレイアウト（OFFで従来の1カラム表示）
  // TODO(3カラム移行完了後に撤去): 新3カラムを本番で検証しOKになったら、
  //   このフラグ / LegacyOverview.vue / 旧 ExtensionCandidateCourses.vue / 旧 CourseHistories.vue を削除し、
  //   Overview.vue を唯一の実装にする（延長候補・コース履歴ロジックの二重保持を解消）。
  //   それまではLegacy側はバグ修正のみとし、新機能は新composable側に寄せる。
  useTableOverview3Column: false,
}

// 値を Boolean に正規化する。既定値に無いキーもそのまま保持する（キーの定義はサーバーが権威）。
const normalizeFlags = source => {
  const result = { ...DEFAULT_FLAGS }
  if (!source) return result

  Object.keys(source).forEach(key => {
    result[key] = Boolean(source[key])
  })

  return result
}

// 旧ユーザー単位フラグを捨てる。引き継ぎが済んだ端末で呼ぶ。
// TODO(移行完了後に撤去): 数リリース後にこの関数と LEGACY_STORAGE_KEY は消す
export const cleanupLegacyFeatureFlags = () => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch (_) {
    // localStorage が使えない端末では何もしない
  }
}

// 旧キーに残っている ON のフラグを返す（OFF は返さない）
const readLegacyEnabledFlags = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || 'null')
    if (!parsed) return {}

    return Object.keys(parsed)
      .filter(key => Boolean(parsed[key]))
      .reduce((result, key) => ({ ...result, [key]: true }), {})
  } catch (_) {
    return {}
  }
}

const readCache = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')

    return { clubId: parsed?.clubId ?? null, flags: normalizeFlags(parsed?.flags) }
  } catch (_) {
    return { clubId: null, flags: { ...DEFAULT_FLAGS } }
  }
}

const { currentClub } = useCurrentData()
const currentClubId = () => currentClub.value?.id ?? null

const cache = readCache()

// キャッシュが別店舗のものなら使わない（店舗切替直後に前の店舗の設定で描画しないため）
const isSameClub = cache.clubId === null || currentClubId() === null || cache.clubId === currentClubId()

const flags = ref(isSameClub ? cache.flags : { ...DEFAULT_FLAGS })
const definitions = ref([])
const isFetching = ref(false)

const writeCache = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ clubId: currentClubId(), flags: flags.value }))
  } catch (_) {
    // localStorage が使えない端末でも致命的ではない（サーバー値で動く）
  }
}

// 移行期間の引き継ぎ。この端末の旧 localStorage に ON が残っていたら、それを店舗設定へ上げる。
// 「どの端末の値が店舗全体になるのか」を無理なく決めるため、次の条件を全部満たすときだけ動かす。
//   1. ON のフラグだけを送る（OFF は送らない＝既にONの店舗を他の端末が戻してしまわない）
//   2. 店舗側が一度も設定されていない（configured=false）ときだけ。UI/admin_v2/rake で
//      一度でも設定された店舗は、端末に残った古い値では上書きされない
//   3. 保存できたら旧キーを削除＝1端末1回きり。権限不足(403)や通信失敗ならキーを残して次回に回す
// TODO(移行完了後に撤去): 数リリース後にこの関数ごと消す
export const migrateLegacyFeatureFlags = async configured => {
  const legacyEnabled = readLegacyEnabledFlags()
  if (Object.keys(legacyEnabled).length === 0) {
    // 引き継ぐものが無い端末は、残っている空の旧キーだけ掃除する
    cleanupLegacyFeatureFlags()

    return flags.value
  }

  // 店舗として設定済みなら引き継がない。この端末の旧キーはもう不要なので捨てる
  if (configured) {
    cleanupLegacyFeatureFlags()

    return flags.value
  }

  try {
    const res = await ClubFeatureFlagApi.updateFeatureFlags(legacyEnabled)
    flags.value = normalizeFlags(res?.data?.featureFlags)
    writeCache()
    cleanupLegacyFeatureFlags()
  } catch (_) {
    // 権限不足や通信失敗。旧キーは残すので、権限のある人が開いたときに引き継がれる
  }

  return flags.value
}

// サーバーの値で ref を差し替える。唯一の取得経路。
// ログイン応答や店舗切替応答にも club.featureFlags が乗るが、あちらはデフォルト補完も
// 未定義キーの除去もされていない生値なので参照しない。
export const fetchFeatureFlags = async () => {
  if (isFetching.value) return flags.value

  isFetching.value = true
  try {
    const res = await ClubFeatureFlagApi.getFeatureFlags()
    flags.value = normalizeFlags(res?.data?.featureFlags)
    definitions.value = res?.data?.definitions ?? []
    writeCache()
    await migrateLegacyFeatureFlags(res?.data?.configured)
  } catch (_) {
    // 取得できないときはキャッシュ/既定値のまま動かす（画面は止めない）
  } finally {
    isFetching.value = false
  }

  return flags.value
}

// 楽観更新 → 保存 → 失敗したら巻き戻す。呼び出し側は失敗を catch して通知する。
export const setFeatureFlag = async (key, value) => {
  const previous = { ...flags.value }
  flags.value = { ...flags.value, [key]: Boolean(value) }

  try {
    const res = await ClubFeatureFlagApi.updateFeatureFlags({ [key]: Boolean(value) })
    flags.value = normalizeFlags(res?.data?.featureFlags)
    writeCache()

    return flags.value
  } catch (error) {
    flags.value = previous
    throw error
  }
}

// サーバー応答やテストから ref をまとめて差し替える（保存はしない）
export const setFeatureFlags = next => {
  flags.value = normalizeFlags(next)
}

// タブ復帰時に取り直す。開きっぱなしのフロア端末が古い設定で動き続けるのを防ぐ。
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    if (!currentClubId()) return // 未ログイン時は叩かない

    fetchFeatureFlags()
  })
}

export default () => ({
  flags,
  definitions,
  isFetching,
})
